import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Typography,
  Dropdown,
  Drawer,
  Descriptions,
  Tabs,
  Timeline,
  Badge,
  AutoComplete
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UserOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
  LaptopOutlined,
  ClockCircleOutlined,
  FilterOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { API_BASE_URL } from '../../appConst';
import styles from './UserManagement.module.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [userDetailDrawer, setUserDetailDrawer] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Debounce timer ref
  const debounceTimer = useRef(null);

  const API_URL = API_BASE_URL;

  // Debounced search - for local filtering
  const debouncedSearch = useCallback((value) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearchText(value);
    }, 300); // 300ms delay for local filtering
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching users with token:', token ? 'Token exists' : 'No token');

      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('User fetch response:', response.data);

      if (response.data.success) {
        setUsers(response.data.users);
        console.log('Users loaded:', response.data.users.length);
      } else {
        message.error(response.data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      message.error(error.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Create user
  const handleCreateUser = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/users`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        message.success('User created successfully');
        setModalVisible(false);
        form.resetFields();
        fetchUsers();
      } else {
        message.error(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user');
    }
  };

  // Update user
  const handleUpdateUser = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/users/${selectedUser.id}`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        message.success('User updated successfully');
        setEditModalVisible(false);
        editForm.resetFields();
        setSelectedUser(null);
        fetchUsers();
      } else {
        message.error(response.data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Failed to update user');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success('User deleted successfully');
        fetchUsers();
      } else {
        message.error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Failed to delete user');
    }
  };

  // Bulk operations
  const handleBulkAction = async (action) => {
    if (!selectedRowKeys.length) {
      message.warning('Please select users first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/users/bulk`,
        { user_ids: selectedRowKeys, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success(response.data.message);
        setSelectedRowKeys([]);
        fetchUsers();
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error('Bulk operation failed');
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Users exported successfully');
    } catch (error) {
      message.error('Failed to export users');
    }
  };

  // View user details
  const viewUserDetails = async (user) => {
    setDetailUser(user);
    setUserDetailDrawer(true);

    // Fetch login history
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/users/${user.id}/login-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setLoginHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
    }

    // Fetch sessions
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/users/${user.id}/sessions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setUserSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  // Revoke session
  const revokeSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/sessions/${sessionId}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success('Session revoked successfully');
        viewUserDetails(detailUser); // Refresh
      }
    } catch (error) {
      message.error('Failed to revoke session');
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      full_name: user.full_name,
      role: user.role,
      active: user.active
    });
    setEditModalVisible(true);
  };

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Filtered users
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = user.username.toLowerCase().includes(searchText.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchText.toLowerCase());

    // Role filter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    // Status filter
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user.active) ||
      (statusFilter === 'inactive' && !user.active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      sorter: (a, b) => a.id - b.id,
      render: (id) => <span className="user-id">{id}</span>,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <Space size={8}>
          <UserOutlined />
          <span className="username-text">{text}</span>
        </Space>
      ),
      sorter: (a, b) => a.username.localeCompare(b.username),
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => <span className="full-name-text">{text || '-'}</span>,
      sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => a.role.localeCompare(b.role),
      render: (role) => (
        <Tag
          bordered
          color={role === 'admin' ? 'red' : 'blue'}
        >
          {role.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      sorter: (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1),
      render: (active) => (
        <Space size={6}>
          <span className={`status-dot ${active ? 'status-active' : 'status-inactive'}`} />
          <span className="status-text">
            {active ? 'Active' : 'Inactive'}
          </span>
        </Space>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <span className="date-text">
          {date ? dayjs(date).tz('Asia/Kolkata').format('MMM DD, YYYY') : '-'}
        </span>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date) => (
        <span className="date-text">
          {date ? dayjs(date).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A') : 'Never'}
        </span>
      ),
      sorter: (a, b) => {
        if (!a.last_login) return 1;
        if (!b.last_login) return -1;
        return new Date(a.last_login) - new Date(b.last_login);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => viewUserDetails(record)}
            className="action-view-btn"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditModal(record)}
            className="action-edit-btn"
          />
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              className="action-delete-btn"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const bulkMenuItems = [
    {
      key: 'activate',
      label: 'Activate Selected',
      icon: <CheckOutlined />,
      onClick: () => handleBulkAction('activate')
    },
    {
      key: 'deactivate',
      label: 'Deactivate Selected',
      icon: <CloseOutlined />,
      onClick: () => handleBulkAction('deactivate')
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      label: 'Delete Selected',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: 'Delete Selected Users',
          content: `Are you sure you want to delete ${selectedRowKeys.length} user(s)?`,
          okText: 'Yes, Delete',
          okType: 'danger',
          onOk: () => handleBulkAction('delete')
        });
      }
    },
  ];

  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      <Card style={{ overflow: 'visible' }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
            <div></div>
            <Space className="user-management-actions" style={{ flexWrap: 'wrap' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchUsers}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                Export CSV
              </Button>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => setModalVisible(true)}
              >
                Create New User
              </Button>
            </Space>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className={styles.searchWrapper} style={{ flex: 1, minWidth: 280, maxWidth: 500 }}>
                <AutoComplete
                  value={searchText}
                  onChange={(value) => {
                    debouncedSearch(value);
                  }}
                  onSelect={(value) => {
                    if (debounceTimer.current) {
                      clearTimeout(debounceTimer.current);
                    }
                    setSearchText(value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (debounceTimer.current) {
                        clearTimeout(debounceTimer.current);
                      }
                      setSearchText(e.target.value);
                    }
                  }}
                  placeholder="Search by username or name..."
                  style={{ width: '100%' }}
                  size="large"
                  allowClear
                  onClear={() => setSearchText('')}
                />
              </div>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                size="large"
                style={{
                  borderRadius: '8px',
                  height: '48px',
                  borderColor: showFilters ? '#B4A0F7' : '#e8e8e8',
                  color: showFilters ? '#B4A0F7' : undefined
                }}
              >
                Filters
              </Button>
            </div>

            {showFilters && (
              <div style={{
                padding: '16px',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                background: '#F9FAFB',
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Role:</Text>
                  <Select
                    value={roleFilter}
                    onChange={setRoleFilter}
                    style={{ width: 120 }}
                  >
                    <Option value="all">All</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="user">User</Option>
                  </Select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Status:</Text>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 120 }}
                  >
                    <Option value="all">All</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </div>
                <Button
                  size="small"
                  onClick={() => {
                    setRoleFilter('all');
                    setStatusFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {selectedRowKeys.length > 0 && (
              <Space style={{ flexWrap: 'wrap' }}>
                <Text>Selected {selectedRowKeys.length} user(s)</Text>
                <Dropdown menu={{ items: bulkMenuItems }}>
                  <Button icon={<MoreOutlined />}>
                    Bulk Actions
                  </Button>
                </Dropdown>
              </Space>
            )}
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredUsers}
            loading={loading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredUsers.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total) => `Total ${total} users`,
              position: ['bottomCenter'],
              onChange: (page, newPageSize) => {
                setCurrentPage(page);
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }
            }}
            scroll={{ x: true, y: 'calc(100vh - 420px)' }}
            onChange={(pagination, filters, sorter) => {
              console.log('Table changed:', { pagination, filters, sorter });
            }}
          />
        </div>
      </Card>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'Username must be at least 3 characters' }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            initialValue="user"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select>
              <Option value="user">User</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="full_name"
            label="Full Name"
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
          >
            <Select>
              <Option value="user">User</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="active"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item
            name="password"
            label="New Password (optional)"
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Leave empty to keep current password" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setSelectedUser(null);
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* User Detail Drawer */}
      <Drawer
        title={`User Details: ${detailUser?.username}`}
        placement="right"
        width={720}
        onClose={() => setUserDetailDrawer(false)}
        open={userDetailDrawer}
      >
        {detailUser && (
          <Tabs defaultActiveKey="1">
            <TabPane tab="Overview" key="1">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="ID">{detailUser.id}</Descriptions.Item>
                <Descriptions.Item label="Username">{detailUser.username}</Descriptions.Item>
                <Descriptions.Item label="Full Name">{detailUser.full_name}</Descriptions.Item>
                <Descriptions.Item label="Role">
                  <Tag color={detailUser.role === 'admin' ? 'red' : 'blue'}>
                    {detailUser.role.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge
                    status={detailUser.active ? 'success' : 'default'}
                    text={detailUser.active ? 'Active' : 'Inactive'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {detailUser.created_at ? dayjs(detailUser.created_at).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Login">
                  {detailUser.last_login ? dayjs(detailUser.last_login).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A') : 'Never'}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>

            <TabPane tab={`Login History (${loginHistory.length})`} key="2">
              <Timeline>
                {loginHistory.map((item) => (
                  <Timeline.Item
                    key={item.id}
                    dot={item.success ? <CheckOutlined style={{ color: 'green' }} /> : <CloseOutlined style={{ color: 'red' }} />}
                    color={item.success ? 'green' : 'red'}
                  >
                    <Space direction="vertical" size="small">
                      <Text strong>{item.success ? 'Successful Login' : 'Failed Login'}</Text>
                      {!item.success && <Text type="danger">{item.failure_reason}</Text>}
                      <Space>
                        <ClockCircleOutlined />
                        <Text type="secondary">{dayjs(item.login_time).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A')}</Text>
                      </Space>
                      <Space>
                        <LaptopOutlined />
                        <Text type="secondary">{item.device_type} - {item.ip_address}</Text>
                      </Space>
                    </Space>
                  </Timeline.Item>
                ))}
              </Timeline>
            </TabPane>

            <TabPane tab={`Active Sessions (${userSessions.length})`} key="3">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {userSessions.map((session) => (
                  <Card key={session.id} size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong>Session {session.id}</Text>
                        {session.revoked ? (
                          <Tag color="red">Revoked</Tag>
                        ) : (
                          <Popconfirm
                            title="Revoke Session"
                            description="Are you sure you want to revoke this session?"
                            onConfirm={() => revokeSession(session.id)}
                          >
                            <Button size="small" danger>Revoke</Button>
                          </Popconfirm>
                        )}
                      </div>
                      <Text type="secondary">IP: {session.ip_address}</Text>
                      <Text type="secondary">Created: {dayjs(session.created_at).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A')}</Text>
                      <Text type="secondary">Expires: {dayjs(session.expires_at).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A')}</Text>
                      {session.last_activity && (
                        <Text type="secondary">Last Activity: {dayjs(session.last_activity).tz('Asia/Kolkata').format('MMM DD, YYYY hh:mm A')}</Text>
                      )}
                    </Space>
                  </Card>
                ))}
                {userSessions.length === 0 && <Text type="secondary">No active sessions</Text>}
              </Space>
            </TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
};

export default UserManagement;
